import React, { useState, useEffect } from "react";

const EditAdditionalInfo = ({
  id_R,
  editAdditionalInfoOpen,
  editAdditionalDetails,
  setEditAdditionalDetails,
  handleEditAdditionalInformation,
  handleCloseEditAdditionalInfo,
  counties,
}) => {
  const [subCounties, setSubCounties] = useState([]);
  const [kraPinError, setKraPinError] = useState("");

  // ✅ KRA PIN validation
  // Format: A123456789X
  const validateKraPin = (pin) => {
    const kraRegex = /^[A-Z]\d{9}[A-Z]$/;
    return kraRegex?.test(pin);
  };

  const handleKraPinChange = (e) => {
    const value = e?.target?.value?.toUpperCase()?.trim();

    setEditAdditionalDetails((prev) => ({
      ...prev,
      kraPin: value,
    }));

    // Only validate when user enters a value
    if (value !== "" && !validateKraPin(value)) {
      setKraPinError("Please enter a valid KRA PIN (e.g. A123456789X)");
    } else {
      setKraPinError("");
    }
  };

  useEffect(() => {
    const selectedCounty = counties?.find(
      (c) => c?.county === editAdditionalDetails?.county,
    );
    if (selectedCounty) {
      setSubCounties(selectedCounty?.sub_counties);
    } else {
      setSubCounties([]);
    }
  }, [editAdditionalDetails?.county, counties]);

  const isFormValid =
    editAdditionalDetails &&
    Object?.entries(editAdditionalDetails)
      ?.filter(([key]) => key !== "kraPin")
      ?.every(([, val]) => val && val?.toString()?.trim() !== "") &&
    kraPinError === "";

  return (
    <>
      <div
        id={id_R}
        className={`${
          editAdditionalInfoOpen ? "flex" : "hidden"
        } items-center justify-center fixed inset-0 z-50 overflow-auto bg-gray-800 bg-opacity-50`}
      >
        <div className="relative flex items-center fixed min-h-screen max-w-[100%] h-full md:h-auto">
          <div className="relative justify-end bg-white shadow max-w-[100%] mx-auto min-h-[80%] w-[55vw]">
            <div
              onClick={handleCloseEditAdditionalInfo}
              className="absolute top-[5px] right-[10px] cursor-pointer text-[24px] flex items-center justify-center"
            >
              X
            </div>
            <div className="p-[20px]">
              <div className="flex items-center gap-[10px] mb-[30px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M17.0874 7.48148H4.09029L10.0602 1.50698L8.54369 0L0 8.55026L8.54369 17.1005L10.0495 15.5935L4.09029 9.61905H17.0874V7.48148Z"
                    fill="black"
                  />
                </svg>
                <p className="text-[20px] font-bold text-[#000]">
                  Edit customer additional information
                </p>
              </div>
              <p className="text-[#000] text-[15px] mb-[20px]">
                ⚠️ All the additional information below is critical system
                therefore ensure you are providing correct information. Certain
                details cannot be edited as they has been verified through legal
                identity documents.
              </p>
              <div className="flex items-center mb-[20px]">
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="firstname">KRA PIN (Optional)</label>
                  <input
                    type="text"
                    value={editAdditionalDetails?.kraPin || ""}
                    onChange={handleKraPinChange}
                    maxLength={11}
                    className={`w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] focus:outline-none ${
                      kraPinError ? "border-red-500" : "border-[#9A9A9A]"
                    }`}
                  />
                  {kraPinError && (
                    <span className="text-red-500 text-sm mt-1">
                      {kraPinError}
                    </span>
                  )}
                </div>
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="firstname">Monthly Income</label>
                  <input
                    type="text"
                    value={editAdditionalDetails?.income_range}
                    onChange={(e) =>
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        income_range: e.target.value,
                      }))
                    }
                    className="w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] border-[#9A9A9A] focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex items-center mb-[20px]">
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="employmentType">Employment Type</label>
                  <select
                    id="employmentType"
                    value={editAdditionalDetails?.employment_type}
                    onChange={(e) =>
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        employment_type: e.target.value,
                      }))
                    }
                    className="w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] border-[#9A9A9A] focus:outline-none"
                  >
                    <option value="">Select Employment Type</option>
                    <option value="Employed">Employed</option>
                    <option value="Unemployed">Unemployed</option>
                    <option value="Self employed">Self employed</option>
                    <option value="Student">Student</option>
                  </select>
                </div>
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="firstname">Job Title</label>
                  <input
                    type="text"
                    value={editAdditionalDetails?.occupation}
                    onChange={(e) =>
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        occupation: e.target.value,
                      }))
                    }
                    className="w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] border-[#9A9A9A] focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex items-center mb-[20px]">
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="firstname">Country of Residence</label>
                  <input
                    type="text"
                    value={editAdditionalDetails?.country_of_residence}
                    onChange={(e) =>
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        country_of_residence: e.target.value,
                      }))
                    }
                    className="w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] border-[#9A9A9A] focus:outline-none"
                  />
                </div>
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="county">County</label>
                  <select
                    name="county"
                    id="county"
                    value={editAdditionalDetails?.county || ""}
                    onChange={(e) => {
                      const selectedCounty = e.target.value;

                      const countyData = counties?.find(
                        (c) => c?.county === selectedCounty,
                      );

                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        county: selectedCounty,
                        subcounty: "",
                      }));

                      setSubCounties(countyData ? countyData.sub_counties : []);
                    }}
                    className="w-[90%] h-10 px-4 rounded-[4px] border-[1px] border-[#000]"
                  >
                    <option value="">Select county</option>
                    {counties?.map((county) => (
                      <option key={county?.id} value={county?.county}>
                        {county?.county}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center mb-[20px]">
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="subCounty">Sub-County</label>
                  <select
                    name="subCounty"
                    id="subCounty"
                    value={editAdditionalDetails?.subcounty || ""}
                    onChange={(e) => {
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        subcounty: e.target.value,
                      }));
                    }}
                    className="w-[90%] h-10 px-4 rounded-[4px] border-[1px] border-[#000]"
                    disabled={!editAdditionalDetails?.county}
                  >
                    <option value="">Select sub-county</option>
                    {subCounties?.map((subCounty) => (
                      <option key={subCounty} value={subCounty}>
                        {subCounty}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex w-[50%] flex-col">
                  <label htmlFor="firstname">Physical Address</label>
                  <input
                    type="text"
                    value={editAdditionalDetails?.physical_address}
                    onChange={(e) =>
                      setEditAdditionalDetails((prev) => ({
                        ...prev,
                        physical_address: e.target.value,
                      }))
                    }
                    className="w-[90%] h-[40px] px-[10px] mt-[5px] border-[1px] border-[#9A9A9A] focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end px-5 mt-16">
                <button
                  onClick={handleEditAdditionalInformation}
                  className={`h-[50px] px-[20px] rounded-[5px] transition-colors duration-200 ${
                    isFormValid
                      ? "bg-[#005C53] text-white cursor-pointer"
                      : "bg-[#D9D9D9] text-[#FFFFFF] cursor-not-allowed"
                  }`}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditAdditionalInfo;
